// src/auth/authClient.js
// ✅ Clean + stable auth/api client for Pohřebka
// - inline getApiBase()
// - multi-key token read
// - exports apiFetch/apiJson + aliases

function getApiBase() {
  try {
    const raw = (import.meta?.env?.VITE_API_BASE_URL || "").trim();
    if (raw) return raw.replace(/\/+$/, "");
  } catch (e) {}
  // default prod
  return "";
}

// ✅ compat export (legacy)
export const API_BASE_URL = getApiBase();



// ✅ compat alias (legacy)
export const API_BASE = API_BASE_URL;
const TOKEN_KEYS = ["pohrebka_token", "auth_token", "pspohrebka_token", "token"];

// ✅ compat alias (legacy)
export function getToken() {
  return getAuthToken();
}

export function getAuthToken() {
  try {
    for (const k of TOKEN_KEYS) {
      const v = localStorage.getItem(k);
      if (v && String(v).trim()) return String(v).trim();
    }
    return "";
  } catch (e) {
    return "";
  }
}

export function setToken(token) {
  const t = String(token || "").trim();
  try {
    if (!t) return;
    // hlavní klíč
    localStorage.setItem("pohrebka_token", t);
    // kompatibilita pro starší části app
    localStorage.setItem("token", t);
  } catch (e) {}
}

export function clearToken() {
  try {
    for (const k of TOKEN_KEYS) localStorage.removeItem(k);
  } catch (e) {}
}

function isAbsoluteUrl(u) {
  return /^https?:\/\//i.test(String(u || ""));
}

function joinUrl(base, path) {
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "");
  if (!p) return b;
  if (p.startsWith("/")) return b + p;
  return b + "/" + p;
}

/**
 * apiFetch(url, options)
 * - url může být "/api/..." nebo plná URL
 * - options.body může být objekt (automaticky JSON.stringify)
 * - automaticky přidá Authorization: Bearer <token> pokud existuje
 * - vrací Response
 */
export async function apiFetch(url, options = {}) {
  const method = String(options.method || "GET").toUpperCase();
  const canHaveBody = !["GET", "HEAD"].includes(method);

  const token = getAuthToken();

  // headers
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  // body normalize
  let finalBody = options.body;
  const isPlainObject =
    finalBody &&
    typeof finalBody === "object" &&
    !(finalBody instanceof FormData) &&
    !(finalBody instanceof Blob) &&
    !(finalBody instanceof ArrayBuffer);

  if (canHaveBody && isPlainObject) {
    if (!headers["Content-Type"] && !headers["content-type"]) {
      headers["Content-Type"] = "application/json";
    }
    finalBody = JSON.stringify(finalBody);
  }

  const base = getApiBase();
  const finalUrl = isAbsoluteUrl(url) ? String(url) : joinUrl(base, String(url));

  const res = await fetch(finalUrl, {
    ...options,
    method,
    headers,
    ...(canHaveBody ? { body: finalBody } : {}),
  });

  return res;
}

/**
 * apiJson(url, options)
 * - postaví na apiFetch
 * - vrací parsed JSON objekt
 * - když není JSON, vrací { _raw: "..." }
 * - při !ok vyhodí Error se status/data/url
 */
export async function apiJson(url, options = {}) {
  const res = await apiFetch(url, options);

  const txt = await res.text().catch(() => "");

  let json = null;
  try {
    json = txt ? JSON.parse(txt) : null;
  } catch {
    json = { _raw: txt };
  }

  if (!res.ok) {
    const msg = json?.error || json?.message || json?._raw || `HTTP ${res.status}`;
    throw Object.assign(new Error(msg), {
      status: res.status,
      data: json,
      url: isAbsoluteUrl(url) ? String(url) : joinUrl(getApiBase(), String(url)),
    });
  }

  return json;
}

// ---- convenience aliases (kvůli kompatibilitě starších komponent) ----
export const doGetJson = (url) => apiJson(url, { method: "GET" });
export const doPostJson = (url, body) =>
  apiJson(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });


// ✅ compat logout (legacy)
export async function logout() {
  try {
    // best-effort server logout (nevadí když selže)
    await apiFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
  } catch (e) {}
  try { clearToken(); } catch (e) {}
  return { ok: true };
}

// -----------------------------
// auth API
// -----------------------------
export async function login(username, password) {
  const r = await apiJson("/api/auth/login", {
    method: "POST",
    body: { username, password }, // apiFetch to zjsonuje
  });

  // ✅ accept multiple token keys (backend variants)
  const t =
    r?.token ||
    r?.access_token ||
    r?.accessToken ||
    r?.jwt ||
    r?.data?.token ||
    r?.data?.access_token;

  if (t) setToken(t);

  return r;
}

export async function me() {
  return apiJson("/api/auth/me", { method: "GET" });
}
